import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Button,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Breadcrumbs,
  CircularProgress,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { Folder, InsertDriveFile, MoreVert, Add, Image } from '@mui/icons-material'
import axiosIct from '../../utils/axios'
import { toast } from 'react-toastify'

import { ReactComponent as DocIcon } from '../../icons/doc.svg'
import { ReactComponent as PdfIcon } from '../../icons/pdf.svg'
import { ReactComponent as PptIcon } from '../../icons/ppt.svg'
import { ReactComponent as XlsIcon } from '../../icons/xls.svg'
import './FileManagerDrive.scss'

export default function FileManagerPage(): React.ReactElement {
  const clientId: string = new URLSearchParams(location.search).get('Client') ?? ''
  const [loading, setLoading] = useState(false)
  const [files, setFiles] = useState<any[]>([])
  const [currentPath, setCurrentPath] = useState<string>('')
  const [renameFolderName, setRenameFolderName] = useState<string>('')
  const [newFolderName, setNewFolderName] = useState<string>('')
  const [newMenuAnchor, setNewMenuAnchor] = useState<null | HTMLElement>(null) // For New button dropdown menu
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null) // For MoreVert button dropdown menu
  const [currentItem, setCurrentItem] = useState<any>(null) // To track the currently selected file/folder for the "More" dropdown
  const [isDragging, setIsDragging] = useState(false)
  const [openNewFolderDialog, setOpenNewFolderDialog] = useState(false)
  const [openRenameDialog, setOpenRenameDialog] = useState(false)

  const fileInputRef = useRef<HTMLInputElement | null>(null); // Ref for file input
  const folderInputRef = useRef<HTMLInputElement | null>(null); // Ref for folder input

  const fetchFiles = async (): Promise<void> => {
    setLoading(true)
    try {
      const response = await axiosIct.get(`/files/?clientId=${clientId}&path=${currentPath}`)
      setFiles(response.data)
    } catch (error) {
      toast.error('Error fetching files', { theme: 'dark' })
    } finally {
      setLoading(false)
    }
  }

  const handleTileClick = (file: any): void => {
    if (file.type === 'folder') {
      setCurrentPath((prev) => `${prev}/${file.name}`)
    }
  }

  const handleNewMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
    setNewMenuAnchor(event.currentTarget)
  }

  const handleMoreMenuClick = (event: React.MouseEvent<HTMLElement>, item: any): void => {
    event.stopPropagation() // Prevent the folder click event
    setMoreMenuAnchor(event.currentTarget)
    setCurrentItem(item)
  }

  const handleMenuClose = (): void => {
    setNewMenuAnchor(null)
    setMoreMenuAnchor(null)
    setCurrentItem(null)
  }

  const handleAddFile = async (file: File): Promise<void> => {
    setLoading(true)
    const formData = new FormData()
    formData.append('clientId', clientId)
    formData.append('path', currentPath)
    formData.append('file', file) // Append the selected file

    try {
      const response = await axiosIct.post('/files/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      toast.success(response.data.message)
      await fetchFiles() // Re-fetch the file list after upload
    } catch (error) {
      toast.error('Error uploading file')
    }
    setLoading(false)
  }

  const handleAddFolder = async (files: FileList): Promise<void> => {
    try {
      setLoading(true)
      const formData = new FormData()
      formData.append('clientId', clientId)
      formData.append('path', currentPath)

      // Append all files to the formData
      Array.from(files).forEach((file) => {
        formData.append('file', file) // Append each file in the folder
      })

      const response = await axiosIct.post('/files/folders/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      toast.success(response.data.message)
      await fetchFiles() // Re-fetch the file list after upload
    } catch (error) {
      toast.error('Error uploading folder')
    }
    setLoading(false)
  }

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>): Promise<void> => {
      event.preventDefault()
      event.stopPropagation()
      setIsDragging(false)

      if (event.dataTransfer.files.length > 0) {
        const file = event.dataTransfer.files[0]
        await handleAddFile(file)
      }
    },
    [currentPath, clientId]
  )

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (): void => {
    setIsDragging(false)
  }

  const handleDeleteItem = async (item: any): Promise<void> => {
    setLoading(true)
    try {
      const response = await axiosIct.delete('/files/', {
        params: {
          clientId: clientId,
          fileName: item.name,
          path: currentPath,
        },
      })
      toast.success(response.data.message)
      await fetchFiles() // Re-fetch the file list after deletion
    } catch (error) {
      toast.error('Error deleting file/folder')
    }
    setLoading(false)
  }

  const handleRenameItem = async (): Promise<void> => {
    setOpenRenameDialog(false)
    if (newFolderName.trim() !== '' && renameFolderName !== newFolderName) {
      setLoading(true)
      try {
        const response = await axiosIct.post('/files/rename', null, {
          params: {
            clientId: clientId,
            oldName: renameFolderName,
            newName: newFolderName,
            path: currentPath,
          },
        })
        toast.success(response.data.message)
        await fetchFiles() // Re-fetch the file list after renaming
      } catch (error) {
        toast.error('Error renaming file/folder')
      }
    }
    setLoading(false)
    setMoreMenuAnchor(null)
    setNewFolderName('')
    setRenameFolderName('')
  }

  const handleCreateFolder = async (): Promise<void> => {
    setOpenNewFolderDialog(false)
    if (!newFolderName.trim()) {
      toast.error('Folder name cannot be empty', { theme: 'dark' })
      return
    }
    setLoading(true)
    try {
      await axiosIct.post('/files/folders', {
        clientId,
        path: currentPath,
        folderName: newFolderName,
      })
      toast.success('Folder created successfully', { theme: 'dark' })
      setNewFolderName('')
      await fetchFiles()
    } catch (error) {
      toast.error('Error creating folder', { theme: 'dark' })
    } finally {
      setLoading(false)
    }
    setMoreMenuAnchor(null)
    setNewMenuAnchor(null)
  }

  const handleBreadcrumbClick = (index: number): void => {
    const pathParts = currentPath.split('/').slice(0, index + 1)
    setCurrentPath(pathParts.join('/'))
  }

  // Fetch files on path change
  useEffect(() => {
    void fetchFiles()
  }, [currentPath])

  // Filter folders and regular files
  const folders = files.filter((file) => file.type === 'folder')
  const regularFiles = files.filter((file) => file.type !== 'folder')

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase()
    switch (extension) {
      case 'pdf':
        return <PdfIcon />
      case 'docx':
      case 'doc':
      case 'txt':
        return <DocIcon />
      case 'xlsx':
      case 'xls':
      case 'csv':
        return <XlsIcon />
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image fontSize="large" />
      case 'pptx':
      case 'ppt':
        return <PptIcon />
      default:
        return <InsertDriveFile fontSize="large" style={{ color: 'grey' }} />
    }
  }

  const triggerFileInput = (): void => {
    fileInputRef.current?.click()
  }

  const triggerFolderInput = (): void => {
    folderInputRef.current?.click()
  }

  return (
    <div className={`file-manager-container ${isDragging ? 'dragging' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        >
      {isDragging && (
        <div className="drag-and-drop-message">
          <Typography variant="h5" color="primary">
            Drag and Drop Files Here
          </Typography>
        </div>
      )}
      {loading ? (
        <CircularProgress />
      ) : (
        <div 
        className="file-manager-content">
          {/* Sidebar: New button and Home */}
          <div className="sidebar">
            <div className="new-section">
              <Button
                className="new-button"
                startIcon={<Add />}
                onClick={handleNewMenuClick}
                sx={{
                  backgroundColor: '#ffffff',
                  color: 'black',
                  border: '2px solid #b2baba', // Apply custom border styling
                  padding: '5px',
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  textTransform: 'none',
                  minWidth: '100px',
                  '&:hover': {
                    backgroundColor: '#b2baba',
                  },
                }}
              >
                New
              </Button>

              <Menu
                anchorEl={newMenuAnchor}
                open={Boolean(newMenuAnchor)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={triggerFileInput}>
                  Upload File
                  <input
                    type="file"
                    hidden
                    multiple
                    ref={fileInputRef}
                    onChange={(e) => {
                      if (e.target.files) {
                        Array.from(e.target.files).forEach(handleAddFile)
                      }
                    }}
                  />
                </MenuItem>
                <MenuItem onClick={triggerFolderInput}>
                  Upload Folder
                  <input
                    type="file"
                    hidden
                    //@ts-ignore
                    webkitdirectory="true"
                    directory="true"
                    multiple
                    ref={folderInputRef}
                    onChange={(e) => {
                      if (e.target.files) {
                        handleAddFolder(e.target.files)
                      }
                    }}
                  />
                </MenuItem>
                <MenuItem onClick={ () => { setOpenNewFolderDialog(true) }}>New Folder</MenuItem>
              </Menu>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="main-content">
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" style={{ marginBottom: '20px' }}>
              <Typography
                key="root"
                color="inherit"
                onClick={() => setCurrentPath('')}
                style={{ cursor: 'pointer' }}
              >
                Home
              </Typography>
              {currentPath.split('/').filter(Boolean).map((folder, index) => (
                <Typography
                  key={index}
                  color="inherit"
                  onClick={() => handleBreadcrumbClick(index)}
                  style={{ cursor: 'pointer' }}
                >
                  {folder}
                </Typography>
              ))}
            </Breadcrumbs>

            {/* Folders and Files Section */}
            <div className="file-grid">
              {folders.map((file) => (
                <div className="file-card" key={file.id} onClick={() => handleTileClick(file)}>
                  <div className="file-info">
                    <Folder fontSize="large" style={{ color: 'grey' }} />
                    <Typography className="file-name">{file.name}</Typography>
                    <IconButton
                      aria-label="more"
                      onClick={(event) => handleMoreMenuClick(event, file)}
                      className="more-icon"
                    >
                      <MoreVert />
                    </IconButton>
                  </div>
                </div>
              ))}
              {regularFiles.map((file) => (
                <div className="file-card" key={file.id} onClick={() => handleTileClick(file)}>
                  <div className="file-info">
                    {getFileIcon(file.name)}
                    <Typography className="file-name">{file.name}</Typography>
                    <IconButton
                      aria-label="more"
                      onClick={(event) => handleMoreMenuClick(event, file)}
                      className="more-icon"
                    >
                      <MoreVert />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* More Options Menu */}
          {currentItem && (
            <Menu
              anchorEl={moreMenuAnchor}
              open={Boolean(moreMenuAnchor)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => { setRenameFolderName(currentItem.name); setOpenRenameDialog(true) }}>Rename</MenuItem>
              <MenuItem onClick={() => handleDeleteItem(currentItem)}>Delete</MenuItem>
              <MenuItem onClick={() => toast.success('Download functionality is not implemented yet.')}>Download</MenuItem>
            </Menu>
          )}
        </div>
      )}
      <Dialog open={openNewFolderDialog} onClose={() => setOpenNewFolderDialog(false)}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            type="text"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewFolderDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleCreateFolder} color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openRenameDialog} onClose={() => setOpenRenameDialog(false)}>
        <DialogTitle>Rename File/Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="File/Folder Name"
            type="text"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRenameDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={ handleRenameItem } color="primary">
            Rename
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  )
}
